import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const ADDED_LABEL_DURATION = 1600;

/**
 * Adds every product in the "frequently bought together" row to the cart in one
 * request.
 *
 * Mirrors the multi-line add in 'product-form.js': dispatch a CartLinesUpdateEvent
 * carrying a deferred promise so the cart icon and drawer know an add is in
 * flight, POST all lines at once, then resolve that promise with the refreshed
 * cart so those listeners can settle.
 *
 * @typedef {Object} BundleRefs
 * @property {HTMLButtonElement} [button] - The add-set button
 * @property {HTMLElement} [buttonText] - The button's label, swapped on success
 *
 * @extends {Component<BundleRefs>}
 */
class DavittaBundle extends Component {
  requiredRefs = ['button'];

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #labelTimeout;
  /** @type {string | null} */
  #idleLabel = null;
  #pending = false;

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#labelTimeout);
  }

  /** @returns {{ id: number, quantity: number }[]} */
  get #lines() {
    return (this.dataset.variantIds ?? '')
      .split(',')
      .map((id) => Number.parseInt(id.trim(), 10))
      .filter((id) => Number.isFinite(id) && id > 0)
      .map((id) => ({ id, quantity: 1 }));
  }

  addAll = () => {
    // Double-clicking the button would otherwise add the set twice.
    if (this.#pending) return;

    const lines = this.#lines;
    if (!lines.length) return;

    this.#pending = true;
    if (this.refs.button) this.refs.button.disabled = true;

    const deferred = CartLinesUpdateEvent.createPromise();
    const itemCount = lines.reduce((sum, line) => sum + line.quantity, 0);

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: lines.map((line) => ({
          merchandiseId: String(line.id),
          quantity: line.quantity,
        })),
        promise: deferred.promise,
      })
    );

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ items: lines, sections: this.#cartSectionIds() }),
    })
      .then((response) => response.json())
      .then(async (response) => {
        // The Cart AJAX API reports failures with a `status` field on a 4xx body.
        if (response.status) {
          throw new Error(response.message || 'Add to cart failed');
        }

        const cart = await this.#fetchCart();

        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'davitta-bundle',
            sourceId: this.id || 'davitta-bundle',
            itemCount,
            sections: response.sections,
            didError: false,
          },
        });

        this.#showAdded();
      })
      .catch((error) => {
        console.error(error);
        deferred.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Network error while adding the set to the cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        this.#pending = false;
        if (this.refs.button) this.refs.button.disabled = false;
      });
  };

  /**
   * Section ids the cart renders into, so the server returns their refreshed HTML
   * for the drawer to morph in.
   * @returns {string}
   */
  #cartSectionIds() {
    const ids = [];

    for (const component of document.querySelectorAll('cart-items-component')) {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        ids.push(component.dataset.sectionId);
      }
    }

    return ids.join(',');
  }

  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  #showAdded() {
    const label = this.refs.buttonText;
    if (!label) return;

    // Captured once so repeated clicks cannot latch the "added" text as the idle one.
    this.#idleLabel ??= label.textContent;

    label.textContent = Theme.translations.added;
    clearTimeout(this.#labelTimeout);

    this.#labelTimeout = setTimeout(() => {
      if (this.#idleLabel != null) label.textContent = this.#idleLabel;
    }, ADDED_LABEL_DURATION);
  }
}

if (!customElements.get('davitta-bundle-component')) {
  customElements.define('davitta-bundle-component', DavittaBundle);
}
